import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, MapPin, Award, CheckCircle2, IdCard, Megaphone } from "lucide-react";
import api, { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1604212561903-5ca7f041c58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxuaWdlcmlhJTIwcG9saXRpY2FsJTIwY2FtcGFpZ24lMjBjcm93ZHxlbnwwfHx8fDE3ODYwODk2MDh8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [stats, setStats] = useState({ total_lgas: 13, total_wards: 147 });
  useEffect(() => {
    api.get("/structure/lgas").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO} alt="Supporters" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-primary/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-block eyebrow text-secondary mb-4">London Boy Movement · Nasarawa State</span>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              The Grassroots Engine Behind <span className="text-secondary">Sen. Ahmed Aliyu Wadada's</span> Vision.
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-xl leading-relaxed">
              One movement. 13 LGAs. 147 wards. Join thousands of volunteers mobilising for a stronger,
              empowered Nasarawa — right down to your polling unit.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/volunteer" data-testid="hero-join-btn">
                <Button className="rounded-full bg-secondary text-slate-900 font-bold px-8 py-6 text-base hover:bg-secondary/90 hover:-translate-y-1 transition-transform shadow-lg shadow-secondary/30 gap-2">
                  Join the Movement <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/structure" data-testid="hero-find-btn">
                <Button variant="outline" className="rounded-full border-2 border-white text-white bg-white/10 font-bold px-8 py-6 text-base hover:bg-white hover:text-primary transition-colors gap-2">
                  <MapPin className="w-5 h-5" /> Find Your Coordinator
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-6 -mt-16 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: MapPin, n: `${stats.total_lgas}`, l: "Local Govt. Areas" },
            { icon: Users, n: "147", l: "Electoral Wards" },
            { icon: Award, n: "1", l: "Unified Vision" },
            { icon: CheckCircle2, n: "24/7", l: "PVC Helpdesk" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white rounded-2xl border-l-4 border-primary shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-5">
              <s.icon className="w-6 h-6 text-primary mb-2" />
              <div className="font-heading font-black text-3xl text-slate-900">{s.n}</div>
              <div className="text-sm text-muted-foreground">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISION HIGHLIGHT */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="absolute -inset-4 bg-secondary/30 rounded-3xl rotate-3" />
          <div className="relative rounded-3xl shadow-2xl w-full aspect-[4/5] topo-bg flex flex-col items-center justify-center text-center p-8">
            <img src={LOGO_URL} alt="London Boy Movement" className="w-40 h-40 object-contain bg-white rounded-2xl p-3 shadow-lg" />
            <div className="mt-6 font-heading font-extrabold text-white text-2xl">Sen. Ahmed Aliyu Wadada</div>
            <div className="text-secondary text-sm font-semibold mt-1">Official portrait coming soon</div>
          </div>
        </div>
        <div>
          <span className="eyebrow">Wadada's Vision</span>
          <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-3 tracking-tight text-slate-900">Accountability. Empowerment. Development.</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            From the Senate Public Accounts Committee to grassroots empowerment schemes, Senator Wadada's
            record is built on transparency and real impact for the people of Nasarawa.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              [Megaphone, "Legislative Accountability"],
              [Users, "Youth Empowerment"],
              [Award, "Community Projects"],
              [CheckCircle2, "Farmer Support"],
            ].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 bg-muted rounded-xl px-4 py-3">
                <Icon className="w-5 h-5 text-primary shrink-0" />
                <span className="font-semibold text-sm text-slate-800">{t}</span>
              </div>
            ))}
          </div>
          <Link to="/vision"><Button className="mt-8 rounded-full bg-primary hover:bg-primary/90 font-bold px-7 gap-2" data-testid="home-vision-btn">Explore the Vision <ArrowRight className="w-4 h-4" /></Button></Link>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="topo-bg">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center text-white">
          <IdCard className="w-12 h-12 mx-auto text-secondary" />
          <h2 className="font-heading font-black text-3xl md:text-4xl mt-4">Get Your Digital Membership ID</h2>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl mx-auto">
            Register in minutes, get a scannable QR membership card, and connect directly to your ward coordinator.
          </p>
          <Link to="/volunteer"><Button className="mt-8 rounded-full bg-secondary text-slate-900 font-bold px-8 py-6 text-base hover:bg-white transition-colors gap-2" data-testid="home-cta-register">Register Now <ArrowRight className="w-5 h-5" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
